import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { InvestorTypeService } from './investor-type.service';
import { InvestorType, UpsertInvestorTypeRequest } from './investor-type.types';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';

interface InvestorTypeFormValue {
  name: string;
  description: string;
}

type InvestorTypeFormControlName = keyof InvestorTypeFormValue;

@Component({
  selector: 'app-investor-type',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './investor-type.html',
  styleUrl: './investor-type.css',
})
export class InvestorTypeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly nameMaxLength = 30;
  private readonly descriptionMaxLength = 50;
  private readonly datatableId = 'kt_investor_type_datatable';
  private readonly createModalId = 'create_investor_type_modal';
  private readonly editModalId = 'edit_investor_type_modal';
  private readonly deleteConfirmModalId = 'delete_investor_type_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createInvestorTypeForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
  });
  protected readonly editInvestorTypeForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly investorTypes = signal<InvestorType[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingInvestorTypeId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingInvestorTypeId = signal<number | null>(null);
  protected readonly pendingDeleteInvestorTypeId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(private readonly investorTypeService: InvestorTypeService) {}

  ngOnInit(): void {
    this.loadInvestorTypes();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: InvestorTypeFormControlName): boolean {
    return this.createFormSubmitted() && this.createInvestorTypeForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: InvestorTypeFormControlName): boolean {
    return this.editFormSubmitted() && this.editInvestorTypeForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: InvestorTypeFormControlName): boolean {
    const control = this.createInvestorTypeForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: InvestorTypeFormControlName): boolean {
    const control = this.createInvestorTypeForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: InvestorTypeFormControlName): boolean {
    const control = this.editInvestorTypeForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: InvestorTypeFormControlName): boolean {
    const control = this.editInvestorTypeForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected loadInvestorTypes(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.investorTypeService.getInvestorTypes().subscribe({
      next: (rows) => {
        this.investorTypes.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load investor type data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreateInvestorType(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createInvestorTypeForm.invalid) {
      this.createInvestorTypeForm.markAllAsTouched();
      return;
    }

    const payload = this.toPayload(this.createInvestorTypeForm.getRawValue());

    this.isCreating.set(true);

    this.investorTypeService.createInvestorType(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateInvestorTypeModal();
        this.resetCreateInvestorTypeForm();
        window.KTToast?.show({
          message: 'Investor type created successfully.',
          variant: 'success',
        });
        this.loadInvestorTypes();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create investor type at this time. Please try again.');
      },
    });
  }

  protected resetCreateInvestorTypeForm(): void {
    this.createInvestorTypeForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditInvestorTypeModal(investorType: InvestorType): void {
    this.editingInvestorTypeId.set(investorType.id);
    this.editInvestorTypeForm.reset({
      name: investorType.name,
      description: investorType.description,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdateInvestorType(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editInvestorTypeForm.invalid) {
      this.editInvestorTypeForm.markAllAsTouched();
      return;
    }

    const investorTypeId = this.editingInvestorTypeId();
    if (investorTypeId === null) {
      this.editFormError.set('Unable to identify the investor type to update.');
      return;
    }

    const payload = this.toPayload(this.editInvestorTypeForm.getRawValue());
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.investorTypeService.updateInvestorType(investorTypeId, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditInvestorTypeModal();
        this.resetEditInvestorTypeForm();
        window.KTToast?.show({
          message: 'Investor type updated successfully.',
          variant: 'success',
        });
        this.loadInvestorTypes(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update investor type at this time. Please try again.');
      },
    });
  }

  protected resetEditInvestorTypeForm(): void {
    this.editInvestorTypeForm.reset(this.getEmptyFormValue());
    this.editingInvestorTypeId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-investor-type"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-investor-type"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const investorTypeId = this.getInvestorTypeIdFromButton(editButton);
      if (investorTypeId === null) {
        return;
      }

      const investorType = this.investorTypes().find((item) => item.id === investorTypeId);
      if (!investorType) {
        return;
      }

      this.openEditInvestorTypeModal(investorType);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const investorTypeId = this.getInvestorTypeIdFromButton(deleteButton);
    if (investorTypeId === null) {
      return;
    }

    this.openDeleteConfirmationModal(investorTypeId);
  }

  protected cancelDeleteInvestorType(): void {
    this.pendingDeleteInvestorTypeId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDeleteInvestorType(): void {
    const investorTypeId = this.pendingDeleteInvestorTypeId();

    if (investorTypeId === null || this.deletingInvestorTypeId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteInvestorTypeId.set(null);
    this.removeInvestorType(investorTypeId);
  }

  protected removeInvestorType(investorTypeId: number): void {
    if (this.deletingInvestorTypeId() !== null) {
      return;
    }

    this.deletingInvestorTypeId.set(investorTypeId);

    this.investorTypeService.deleteInvestorType(investorTypeId).subscribe({
      next: (result) => {
        this.deletingInvestorTypeId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.investorTypes.update((rows) => rows.filter((item) => item.id !== investorTypeId));
          this.showSuccessAlert('Investor type deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete investor type.',
          result.message,
        );
      },
      error: () => {
        this.deletingInvestorTypeId.set(null);
        this.showDestructiveAlert(
          'Unable to delete investor type.',
          'Unable to delete investor type at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateInvestorTypeForm();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditInvestorTypeForm();
    });
  }

  private closeCreateInvestorTypeModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditInvestorTypeModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(investorTypeId: number): void {
    this.pendingDeleteInvestorTypeId.set(investorTypeId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getInvestorTypeIdFromButton(button: HTMLButtonElement): number | null {
    const investorTypeIdRaw = button.getAttribute('data-investor-type-id');
    const investorTypeId = investorTypeIdRaw ? Number(investorTypeIdRaw) : Number.NaN;

    return Number.isFinite(investorTypeId) ? investorTypeId : null;
  }

  private getEmptyFormValue(): InvestorTypeFormValue {
    return {
      name: '',
      description: '',
    };
  }

  private toPayload(rawValue: InvestorTypeFormValue): UpsertInvestorTypeRequest {
    const description = rawValue.description.trim();

    return {
      name: rawValue.name.trim(),
      description: description ? description : null,
    };
  }
}
